import type { Maybe } from '@adornis/base/utilTypes.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { getContactPersonsForDebitor } from '../../api/getContactPersonsForDebitor.js';
import { searchCompanies } from '../../api/searchCompanies.js';
import { Company } from '../../db/Company.js';
import { ContactPerson } from '../../db/ContactPerson.js';
import type { ExtractedCompanyInfo } from '../../features/information-extraction/db/Company.js';
import type { AbstractMailExtraction } from '../../features/information-extraction/db/MailExtractionResult.js';

interface CompanyDebitorPickerProps {
  /** The requesting company — the only one that is editable today. */
  company: Maybe<ExtractedCompanyInfo>;
  mailProcessing: AbstractMailExtraction;
  isEditing: boolean;
  onClose: () => void;
}

/** The firm's master record, searched by debitor, name or town. */
const renderCompanySearch = (onPicked: (company: Company | null) => void) =>
  DropdownSelectionInput<Company>({
    search: async (searchString: string) => {
      const results = await searchCompanies(searchString)({
        _id: 1,
        debitor: 1,
        name: 1,
        address: 1,
        city: 1,
        countryCode: 1,
        taxId: 1,
      });
      return results ?? [];
    },
    renderString: (c: Company) => `${c.debitor} – ${c.name}`,
    renderItem: (c: Company) => html`
      <div class="py-1">
        <div class="font-medium">${c.debitor ?? '–'} – ${c.name ?? '–'}</div>
        <div class="text-xs text-base-content/50">${c.address ?? ''}, ${c.city ?? '–'}</div>
      </div>
    `,
    resolveID: (c: Company) => c._id,
    placeholder: 'Firma suchen (Debitor, Name, Ort...)',
    onPicked,
  });

/**
 * The contact people of one debitor. The whole list comes back in one call and is filtered
 * here — a firm has a handful of them, and picking one should not wait on the server.
 */
const renderContactSearch = (debitor: Maybe<string>, onPicked: (contact: ContactPerson | null) => void) => {
  if (!debitor) return html`<div class="text-sm text-base-content/50 italic">Bitte zuerst eine Firma auswählen</div>`;
  return DropdownSelectionInput<ContactPerson>({
    search: async (searchString: string) => {
      const results = await getContactPersonsForDebitor(debitor)({
        _id: 1,
        partner: 1,
        firstName: 1,
        lastName: 1,
        email: 1,
      });
      if (!results) return [];
      const needle = searchString.trim().toLowerCase();
      if (!needle) return results;
      return results.filter(c =>
        [c.partner, c.firstName, c.lastName, c.email].filter(Boolean).some(v => String(v).toLowerCase().includes(needle)),
      );
    },
    renderString: (c: ContactPerson) =>
      `${c.partner} – ${[c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || '–'}`,
    renderItem: (c: ContactPerson) => html`
      <div class="py-1">
        <div class="font-medium">${c.partner ?? '–'} – ${[c.firstName, c.lastName].filter(Boolean).join(' ') || '–'}</div>
        <div class="text-xs text-base-content/50">${c.email ?? ''}</div>
      </div>
    `,
    resolveID: (c: ContactPerson) => c._id,
    placeholder: 'Ansprechpartner suchen (Partner-ID, Name, E-Mail...)',
    onPicked,
  });
};

export const CompanyDebitorPicker = stateful((props: CompanyDebitorPickerProps) => {
  const { company, mailProcessing, isEditing, onClose } = props;
  const [version, setVersion] = useState(0);
  // Whether the header was already corrected when this panel opened — the same question
  // ProductEditRow asks, so "Abbrechen" undoes what was changed here and nothing older.
  const [wasCorrected] = useState(mailProcessing.isCompanyCorrected(), [isEditing]);

  if (!company || !isEditing) return nothing;

  const { field } = useForm(company, {}, [company.debitor, version]);

  // Every write goes through `correctCompany`, which freezes what the extraction produced
  // before the first one — the reason the Korrekturen card can show what changed. It returns
  // the very object this form is bound to.
  const handleCompanySelected = (selectedCompany: Company | null) => {
    if (!selectedCompany) return;

    const target = mailProcessing.correctCompany();
    target.debitor = selectedCompany.debitor;
    target.name = selectedCompany.name;
    target.address = selectedCompany.address;
    target.city = selectedCompany.city;
    target.country = selectedCompany.countryCode;
    target.taxId = selectedCompany.taxId;
    // Debitor changed: previous Ansprechpartner no longer applies
    target.partnerID = null;
    target.partnerName = null;
    setVersion(version + 1);
  };

  const handleContactSelected = (selectedContact: ContactPerson | null) => {
    if (!selectedContact) return;
    const target = mailProcessing.correctCompany();
    target.partnerID = selectedContact.partner;
    target.partnerName = [selectedContact.firstName, selectedContact.lastName].filter(Boolean).join(' ') || null;
    setVersion(version + 1);
  };

  const handleSave = async () => {
    await mailProcessing.save();
    onClose();
  };

  // Nothing here is saved yet: drop the correction again so the card does not show a
  // "Korrigiert" that only lives until the next read from the server.
  const handleCancel = () => {
    if (!wasCorrected) mailProcessing.discardCompanyCorrection();
    onClose();
  };

  const handleRevert = async () => {
    await mailProcessing.revertCompanyCorrection();
    onClose();
  };

  return html`
    <div class="bg-base-200/50 border border-base-300 border-t-2 border-t-primary rounded-b-lg p-5 space-y-4 mt-3">
      <!-- Firma aus Stammdaten suchen -->
      <div>
        <label class="text-xs font-semibold text-base-content/60 uppercase tracking-wide mb-1 block">
          Firma aus Stammdaten
        </label>
        ${renderCompanySearch(handleCompanySelected)}
      </div>

      <!-- Firmendaten -->
      <div>
        <label class="text-xs font-semibold text-base-content/60 uppercase tracking-wide mb-1 block">
          Firmendaten
        </label>
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
          ${TextInput({ ...(field('debitor') as any), placeholder: 'Debitor', disabled: true })}
          ${TextInput({ ...(field('name') as any), placeholder: 'Name', disabled: true })}
          ${TextInput({ ...(field('address') as any), placeholder: 'Adresse', disabled: true })}
          ${TextInput({ ...(field('city') as any), placeholder: 'Ort', disabled: true })}
        </div>
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
          ${TextInput({ ...(field('zipCode') as any), placeholder: 'PLZ', disabled: true })}
          ${TextInput({ ...(field('country') as any), placeholder: 'Land', disabled: true })}
          ${TextInput({ ...(field('taxId') as any), placeholder: 'USt-IdNr.', disabled: true })}
          ${TextInput({ ...(field('email') as any), placeholder: 'E-Mail', disabled: true })}
        </div>
      </div>

      <!-- Ansprechpartner -->
      <div>
        <label class="text-xs font-semibold text-base-content/60 uppercase tracking-wide mb-1 block">
          Ansprechpartner
        </label>
        ${renderContactSearch(company.debitor, handleContactSelected)}
        <div class="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          ${TextInput({ ...(field('partnerID') as any), placeholder: 'Partner-ID', disabled: true })}
          ${TextInput({ ...(field('partnerName') as any), placeholder: 'Name', disabled: true })}
        </div>
      </div>

      <!-- Buttons -->
      <div class="flex gap-2 justify-end pt-2 border-t border-base-300">
        ${mailProcessing.isCompanyCorrected()
          ? html`<div
              class="tooltip tooltip-top mr-auto"
              data-tip="Setzt Firma und Ansprechpartner auf die Werte der Extraktion zurück"
            >
              <button class="btn btn-warning btn-sm gap-1" @click=${handleRevert}>
                <x-icon>undo</x-icon> Korrektur zurücksetzen
              </button>
            </div>`
          : nothing}
        <button class="btn btn-ghost btn-sm" @click=${handleCancel}><x-icon>close</x-icon> Abbrechen</button>
        <button class="btn btn-primary btn-sm" @click=${handleSave}><x-icon>check</x-icon> Speichern</button>
      </div>
    </div>
  `;
});
